import { API } from '@/lib/api';
import { useState, useRef, useCallback, useEffect } from "react";
import Map, { Marker, Source, Layer, NavigationControl } from "react-map-gl";
import { MapPin, Navigation, LocateFixed, Route, X, Star } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import axios from "axios";

const MAPBOX_TOKEN = process.env.REACT_APP_MAPBOX_TOKEN;

const ONTARIO_CENTER = { latitude: 43.65, longitude: -79.38 }; // Toronto

// Geocoding search with debounce
const searchAddress = async (query, proximity) => {
  if (!query || query.length < 3) return [];
  const prox = proximity ? `&proximity=${proximity.longitude},${proximity.latitude}` : "";
  const res = await fetch(
    `https://api.mapbox.com/geocoding/v5/mapbox.places/${encodeURIComponent(query)}.json?access_token=${MAPBOX_TOKEN}&country=CA&limit=5${prox}`
  );
  const data = await res.json();
  return (data.features || []).map(f => ({
    name: f.place_name,
    longitude: f.center[0],
    latitude: f.center[1],
  }));
};

const reverseGeocode = async (lng, lat) => {
  const res = await fetch(
    `https://api.mapbox.com/geocoding/v5/mapbox.places/${lng},${lat}.json?access_token=${MAPBOX_TOKEN}&limit=1`
  );
  const data = await res.json();
  return data.features?.[0]?.place_name || `${lat.toFixed(5)}, ${lng.toFixed(5)}`;
};

const fetchRoute = async (start, end) => {
  const res = await fetch(
    `https://api.mapbox.com/directions/v5/mapbox/driving/${start.longitude},${start.latitude};${end.longitude},${end.latitude}?geometries=geojson&overview=full&access_token=${MAPBOX_TOKEN}`
  );
  const data = await res.json();
  if (data.code === "Ok" && data.routes?.[0]) {
    return {
      geometry: data.routes[0].geometry,
      distance: data.routes[0].distance,
      duration: data.routes[0].duration,
    };
  }
  return null;
};

// Address autocomplete input
const AddressInput = ({ label, value, onChange, onSelect, placeholder, testId }) => {
  const [query, setQuery] = useState(value || "");
  const [results, setResults] = useState([]);
  const [open, setOpen] = useState(false);
  const timeoutRef = useRef(null);
  const wrapperRef = useRef(null);

  useEffect(() => { setQuery(value || ""); }, [value]);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const handleChange = (e) => {
    const val = e.target.value;
    setQuery(val);
    onChange(val);
    clearTimeout(timeoutRef.current);
    timeoutRef.current = setTimeout(async () => {
      if (val.length >= 3) {
        const res = await searchAddress(val, ONTARIO_CENTER);
        setResults(res);
        setOpen(true);
      } else {
        setResults([]);
        setOpen(false);
      }
    }, 350);
  };

  const handleSelect = (item) => {
    setQuery(item.name);
    setOpen(false);
    onSelect(item);
  };

  return (
    <div ref={wrapperRef} className="relative">
      <Label className="text-emerald-950 font-medium mb-2 block">{label}</Label>
      <div className="relative">
        <MapPin className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-emerald-600" />
        <Input
          className="input-style pl-10 pr-8"
          placeholder={placeholder}
          value={query}
          onChange={handleChange}
          onFocus={() => results.length > 0 && setOpen(true)}
          data-testid={testId}
        />
        {query && (
          <button
            type="button"
            className="absolute right-2 top-1/2 -translate-y-1/2 text-stone-400 hover:text-stone-600"
            onClick={() => { setQuery(""); onChange(""); setResults([]); setOpen(false); }}
          >
            <X className="w-4 h-4" />
          </button>
        )}
      </div>
      {open && results.length > 0 && (
        <div className="absolute z-50 w-full mt-1 bg-white border border-stone-200 rounded-lg shadow-lg max-h-48 overflow-y-auto">
          {results.map((r, i) => (
            <button
              key={i}
              type="button"
              className="w-full text-left px-3 py-2.5 hover:bg-emerald-50 text-sm text-stone-700 border-b border-stone-100 last:border-0 flex items-start gap-2"
              onClick={() => handleSelect(r)}
              data-testid={`${testId}-result-${i}`}
            >
              <MapPin className="w-3.5 h-3.5 text-emerald-600 mt-0.5 shrink-0" />
              <span className="line-clamp-2">{r.name}</span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

// Main MapLocationPicker component
const MapLocationPicker = ({
  pickupAddress,
  dropoffAddress,
  onPickupChange,
  onDropoffChange,
  onPickupCoordsChange,
  onDropoffCoordsChange,
  onRouteChange,
  singleLocation = false,
  serviceType = null,
  locationLabel = "Location",
  pickupLabel = "Pickup Address *",
  dropoffLabel = "Drop-off Address *",
  pickupPlaceholder = "Search pickup address...",
  dropoffPlaceholder = "Search drop-off address...",
}) => {
  const mapRef = useRef(null);
  const [pickup, setPickup] = useState(null);
  const [dropoff, setDropoff] = useState(null);
  const [route, setRoute] = useState(null);
  const [nearbyPros, setNearbyPros] = useState([]);
  const [selectingMode, setSelectingMode] = useState("pickup"); // pickup | dropoff
  const [viewState, setViewState] = useState({
    ...ONTARIO_CENTER,
    zoom: 11,
  });

  // Auto-detect GPS location on mount
  useEffect(() => {
    if (!navigator.geolocation) return;
    navigator.geolocation.getCurrentPosition(
      async (pos) => {
        const { latitude, longitude } = pos.coords;
        const address = await reverseGeocode(longitude, latitude);
        const point = { longitude, latitude, name: address };
        setPickup(point);
        onPickupChange(address);
        onPickupCoordsChange?.({ latitude, longitude });
        if (mapRef.current) {
          mapRef.current.getMap().flyTo({ center: [longitude, latitude], zoom: 14, duration: 800 });
        }
        if (!singleLocation) setSelectingMode("dropoff");
      },
      () => {},
      { enableHighAccuracy: true, timeout: 8000 }
    );
  }, []);

  // Fetch nearby professionals when serviceType is provided
  useEffect(() => {
    if (!serviceType) return;
    const fetchPros = async () => {
      try {
        const params = { lat: viewState.latitude, lng: viewState.longitude };
        if (serviceType) params.service = serviceType;
        if (pickup) {
          params.pickup_lat = pickup.latitude;
          params.pickup_lng = pickup.longitude;
        }
        const res = await axios.get(`${API}/professionals/nearby`, { params });
        setNearbyPros(res.data.professionals || []);
      } catch {
        setNearbyPros([]);
      }
    };
    fetchPros();
    const interval = setInterval(fetchPros, 15000);
    return () => clearInterval(interval);
  }, [serviceType, pickup]);

  // Fetch route when both points are set
  useEffect(() => {
    if (pickup && dropoff && !singleLocation) {
      fetchRoute(pickup, dropoff).then(r => {
        setRoute(r);
        if (r && onRouteChange) {
          onRouteChange({
            distance_km: r.distance / 1000,
            duration_minutes: r.duration / 60,
            geometry: r.geometry,
          });
        }
      });
    } else if (onRouteChange && (!pickup || !dropoff)) {
      onRouteChange(null);
    }
  }, [pickup, dropoff, singleLocation]);

  // Fit map to show both markers
  useEffect(() => {
    if (pickup && dropoff && mapRef.current) {
      const map = mapRef.current.getMap();
      const bounds = [
        [Math.min(pickup.longitude, dropoff.longitude) - 0.02, Math.min(pickup.latitude, dropoff.latitude) - 0.02],
        [Math.max(pickup.longitude, dropoff.longitude) + 0.02, Math.max(pickup.latitude, dropoff.latitude) + 0.02],
      ];
      map.fitBounds(bounds, { padding: 60, duration: 800 });
    }
  }, [pickup, dropoff]);

  const handleMapClick = useCallback(async (e) => {
    const { lng, lat } = e.lngLat;
    const address = await reverseGeocode(lng, lat);
    const point = { longitude: lng, latitude: lat, name: address };

    if (singleLocation || selectingMode === "pickup") {
      setPickup(point);
      onPickupChange(address);
      onPickupCoordsChange?.({ latitude: lat, longitude: lng });
      if (!singleLocation) setSelectingMode("dropoff");
    } else {
      setDropoff(point);
      onDropoffChange?.(address);
      onDropoffCoordsChange?.({ latitude: lat, longitude: lng });
      setSelectingMode("pickup");
    }
  }, [selectingMode, singleLocation, onPickupChange, onDropoffChange, onPickupCoordsChange, onDropoffCoordsChange]);

  const handlePickupSelect = (item) => {
    setPickup(item);
    onPickupChange(item.name);
    onPickupCoordsChange?.({ latitude: item.latitude, longitude: item.longitude });
    if (mapRef.current) {
      mapRef.current.getMap().flyTo({ center: [item.longitude, item.latitude], zoom: 14, duration: 800 });
    }
    if (!singleLocation) setSelectingMode("dropoff");
  };

  const handleDropoffSelect = (item) => {
    setDropoff(item);
    onDropoffChange?.(item.name);
    onDropoffCoordsChange?.({ latitude: item.latitude, longitude: item.longitude });
    if (mapRef.current && !pickup) {
      mapRef.current.getMap().flyTo({ center: [item.longitude, item.latitude], zoom: 14, duration: 800 });
    }
    setSelectingMode("pickup");
  };

  const useMyLocation = () => {
    if (!navigator.geolocation) {
      toast.error("Geolocation is not supported by your browser");
      return;
    }
    navigator.geolocation.getCurrentPosition(
      async (pos) => {
        const { latitude, longitude } = pos.coords;
        const address = await reverseGeocode(longitude, latitude);
        const point = { longitude, latitude, name: address };
        setPickup(point);
        onPickupChange(address);
        onPickupCoordsChange?.({ latitude, longitude });
        if (mapRef.current) {
          mapRef.current.getMap().flyTo({ center: [longitude, latitude], zoom: 14, duration: 800 });
        }
        if (!singleLocation) setSelectingMode("dropoff");
        toast.success("Location detected");
      },
      () => toast.error("Unable to get your location"),
      { enableHighAccuracy: true }
    );
  };

  const routeGeoJSON = route?.geometry ? {
    type: "Feature",
    geometry: route.geometry,
    properties: {},
  } : null;

  return (
    <div className="space-y-4" data-testid="map-location-picker">
      {/* Address inputs */}
      <div className={singleLocation ? "" : "grid md:grid-cols-2 gap-4"}>
        <AddressInput
          label={singleLocation ? locationLabel : pickupLabel}
          value={pickupAddress}
          onChange={(val) => onPickupChange(val)}
          onSelect={handlePickupSelect}
          placeholder={singleLocation ? "Search address..." : pickupPlaceholder}
          testId="pickup-address-input"
        />
        {!singleLocation && (
          <AddressInput
            label={dropoffLabel}
            value={dropoffAddress}
            onChange={(val) => onDropoffChange?.(val)}
            onSelect={handleDropoffSelect}
            placeholder={dropoffPlaceholder}
            testId="dropoff-address-input"
          />
        )}
      </div>

      {/* Map controls */}
      <div className="flex items-center gap-2 flex-wrap">
        <Button
          type="button"
          variant="outline"
          size="sm"
          onClick={useMyLocation}
          className="text-xs gap-1.5"
          data-testid="use-my-location-btn"
        >
          <LocateFixed className="w-3.5 h-3.5" /> Use My Location
        </Button>
        {!singleLocation && (
          <div className="flex items-center gap-1 text-xs text-stone-500">
            <Navigation className="w-3 h-3" />
            <span>Click map to set {selectingMode === "pickup" ? "pickup" : "drop-off"} point</span>
          </div>
        )}
        {singleLocation && (
          <div className="flex items-center gap-1 text-xs text-stone-500">
            <Navigation className="w-3 h-3" />
            <span>Click map to set location</span>
          </div>
        )}
      </div>

      {/* Map */}
      <div className="rounded-xl overflow-hidden border border-stone-200 shadow-sm" style={{ height: "300px" }} data-testid="map-container">
        <Map
          ref={mapRef}
          {...viewState}
          onMove={(e) => setViewState(e.viewState)}
          onClick={handleMapClick}
          mapStyle="mapbox://styles/mapbox/streets-v12"
          mapboxAccessToken={MAPBOX_TOKEN}
          style={{ width: "100%", height: "100%" }}
          cursor="crosshair"
        >
          <NavigationControl position="top-right" showCompass={false} />

          {pickup && (
            <Marker longitude={pickup.longitude} latitude={pickup.latitude} anchor="bottom">
              <div className="flex flex-col items-center" data-testid="pickup-marker">
                <div className="bg-emerald-600 text-white text-[10px] font-bold px-1.5 py-0.5 rounded-full mb-0.5 shadow">
                  {singleLocation ? "LOC" : "A"}
                </div>
                <div className="w-6 h-6 bg-emerald-600 rounded-full border-2 border-white shadow-lg flex items-center justify-center">
                  <MapPin className="w-3.5 h-3.5 text-white" />
                </div>
              </div>
            </Marker>
          )}

          {dropoff && !singleLocation && (
            <Marker longitude={dropoff.longitude} latitude={dropoff.latitude} anchor="bottom">
              <div className="flex flex-col items-center" data-testid="dropoff-marker">
                <div className="bg-red-600 text-white text-[10px] font-bold px-1.5 py-0.5 rounded-full mb-0.5 shadow">B</div>
                <div className="w-6 h-6 bg-red-600 rounded-full border-2 border-white shadow-lg flex items-center justify-center">
                  <MapPin className="w-3.5 h-3.5 text-white" />
                </div>
              </div>
            </Marker>
          )}

          {routeGeoJSON && (
            <Source id="route" type="geojson" data={routeGeoJSON}>
              <Layer
                id="route-line"
                type="line"
                paint={{
                  "line-color": "#059669",
                  "line-width": 4,
                  "line-opacity": 0.85,
                }}
              />
            </Source>
          )}

          {/* Nearby professional markers */}
          {nearbyPros.map((pro) => {
            const coords = pro.location?.coordinates;
            if (!coords || coords.length !== 2) return null;
            const hasEta = typeof pro.eta_minutes === "number";
            return (
              <Marker key={pro.user_id} longitude={coords[0]} latitude={coords[1]} anchor="center">
                <div className="relative group cursor-pointer" data-testid={`pro-marker-${pro.user_id}`}>
                  {/* Pulse ring */}
                  <div className="absolute inset-0 m-auto w-10 h-10 bg-emerald-400/15 rounded-full animate-ping" />
                  {/* Car icon — realistic top-down silhouette */}
                  <div className="relative drop-shadow-[0_3px_8px_rgba(0,0,0,0.4)]">
                    <svg width="32" height="44" viewBox="0 0 32 44" fill="none" xmlns="http://www.w3.org/2000/svg">
                      {/* Shadow underneath */}
                      <ellipse cx="16" cy="42" rx="10" ry="2" fill="black" opacity="0.15" />
                      {/* Car body */}
                      <path d="M8 12 C8 6, 10 2, 16 2 C22 2, 24 6, 24 12 L24 32 C24 36, 22 40, 16 40 C10 40, 8 36, 8 32 Z" fill="#0f172a" />
                      {/* Hood curve */}
                      <path d="M10 10 C10 6, 12 3.5, 16 3.5 C20 3.5, 22 6, 22 10 L22 14 L10 14 Z" fill="#1e293b" />
                      {/* Windshield */}
                      <path d="M10.5 14.5 L21.5 14.5 L21 18.5 L11 18.5 Z" fill="#38bdf8" opacity="0.6" />
                      {/* Roof */}
                      <rect x="10.5" y="19" width="11" height="8" rx="1" fill="#1e293b" />
                      {/* Rear window */}
                      <path d="M11 27.5 L21 27.5 L21.5 31 L10.5 31 Z" fill="#38bdf8" opacity="0.4" />
                      {/* Trunk */}
                      <path d="M10 31.5 L22 31.5 L22 35 C22 37, 20 38.5, 16 38.5 C12 38.5, 10 37, 10 35 Z" fill="#1e293b" />
                      {/* Left wheels */}
                      <rect x="5.5" y="12" width="3.5" height="7" rx="1.75" fill="#334155" />
                      <rect x="5.5" y="27" width="3.5" height="7" rx="1.75" fill="#334155" />
                      {/* Right wheels */}
                      <rect x="23" y="12" width="3.5" height="7" rx="1.75" fill="#334155" />
                      <rect x="23" y="27" width="3.5" height="7" rx="1.75" fill="#334155" />
                      {/* Headlights */}
                      <circle cx="12" cy="5.5" r="1.2" fill="#fbbf24" opacity="0.9" />
                      <circle cx="20" cy="5.5" r="1.2" fill="#fbbf24" opacity="0.9" />
                      {/* Taillights */}
                      <circle cx="12" cy="37" r="1" fill="#ef4444" opacity="0.8" />
                      <circle cx="20" cy="37" r="1" fill="#ef4444" opacity="0.8" />
                      {/* Center line highlight */}
                      <line x1="16" y1="19.5" x2="16" y2="26.5" stroke="white" strokeWidth="0.5" opacity="0.1" />
                    </svg>
                  </div>
                  {/* ETA badge — always visible when pickup is set */}
                  {hasEta && (
                    <div className="absolute -top-7 left-1/2 -translate-x-1/2 whitespace-nowrap pointer-events-none" data-testid={`eta-badge-${pro.user_id}`}>
                      <div className="bg-emerald-800 text-white text-[10px] font-bold px-2 py-0.5 rounded-full shadow-lg border border-emerald-600/30">
                        {pro.eta_minutes} min
                      </div>
                    </div>
                  )}
                  {/* Hover tooltip */}
                  <div className="absolute bottom-full left-1/2 -translate-x-1/2 mb-8 opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none whitespace-nowrap z-10">
                    <div className="bg-emerald-950 text-white text-xs px-2.5 py-1.5 rounded-lg shadow-lg">
                      <p className="font-semibold">{pro.name}</p>
                      {pro.avg_rating > 0 && (
                        <p className="flex items-center gap-1 mt-0.5">
                          <Star className="w-2.5 h-2.5 text-amber-400 fill-amber-400" />
                          {pro.avg_rating.toFixed(1)}
                        </p>
                      )}
                      {hasEta && (
                        <p className="text-emerald-300 mt-0.5">{pro.distance_km} km &middot; {pro.eta_minutes} min away</p>
                      )}
                    </div>
                  </div>
                </div>
              </Marker>
            );
          })}
        </Map>
      </div>

      {/* Route info */}
      {route && !singleLocation && (
        <div className="flex items-center gap-4 text-sm bg-emerald-50 border border-emerald-200 rounded-lg px-4 py-2.5" data-testid="route-info">
          <Route className="w-4 h-4 text-emerald-700 shrink-0" />
          <span className="text-emerald-900 font-medium">
            {(route.distance / 1000).toFixed(1)} km
          </span>
          <span className="text-emerald-700">
            ~{Math.ceil(route.duration / 60)} min drive
          </span>
        </div>
      )}

      {/* Nearby professionals indicator */}
      {serviceType && nearbyPros.length > 0 && (() => {
        const closestEta = nearbyPros.find(p => typeof p.eta_minutes === "number");
        return (
          <div className="flex items-center justify-between text-sm bg-emerald-50 border border-emerald-200 rounded-lg px-4 py-2.5" data-testid="nearby-pros-count">
            <div className="flex items-center gap-2">
              <div className="w-2.5 h-2.5 bg-emerald-500 rounded-full animate-pulse" />
              <span className="text-emerald-800 font-medium">
                {nearbyPros.length} professional{nearbyPros.length !== 1 ? "s" : ""} available nearby
              </span>
            </div>
            {closestEta && (
              <span className="text-emerald-700 font-semibold" data-testid="closest-eta">
                Fastest: {closestEta.eta_minutes} min away
              </span>
            )}
          </div>
        );
      })()}
    </div>
  );
};

export default MapLocationPicker;
